import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Calendar, MapPin, Users, Coffee, Check, Footprints, Bike, Mountain, PersonStanding, Dumbbell, Clock, AlertCircle, Pencil, X, ChevronDown, ChevronUp, ExternalLink, Copy, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { EventStatusBadge, type EstadoConfirmacion } from "@/components/event-status-badge";
import { ShareEventButton } from "@/components/share-event-button";
import { sendTransactionalEmail } from "@/lib/email/send";
import { getEventAttendeeEmails } from "@/lib/events.functions";
import { getJoinRequestRecipient } from "@/lib/club-requests.functions";
import { CustomizeClubSection } from "@/components/customize-club-section";
import { useAppContexts } from "@/hooks/use-app-context";
import { JoinConsentDialog } from "@/components/join-consent-dialog";


export const Route = createFileRoute("/_authenticated/dashboard")({
  head: () => ({ meta: [{ title: "Inicio — Brulo" }] }),
  component: Dashboard,
});

type EventRow = {
  id: string;
  name: string;
  event_date: string;
  event_time: string;
  meeting_point: string;
  distance_km: number | null;
  organizer_id: string;
  cafe_id: string | null;
  cafe_invitado_id?: string | null;
  organizador_tipo: "club" | "cafeteria";
  tipo_actividad?: "running" | "ciclismo" | "trail" | "caminata" | "otro" | "ambos" | "social" | null;
  estado_confirmacion: EstadoConfirmacion;
  cancelado?: boolean;
  created_at: string;
  clubs: { name: string; type: string } | null;
};
const EVENT_COLS = "id,name,event_date,event_time,meeting_point,distance_km,organizer_id,cafe_id,cafe_invitado_id,organizador_tipo,tipo_actividad,estado_confirmacion,cancelado,created_at,clubs(name,type)";

type InscritosMap = Record<string, { count: number; names: string[] }>;

async function fetchInscritosMap(eventIds: string[]): Promise<InscritosMap> {
  if (eventIds.length === 0) return {};
  const { data: ins } = await supabase
    .from("inscripciones")
    .select("evento_id,usuario_id,fecha_inscripcion")
    .in("evento_id", eventIds)
    .order("fecha_inscripcion");
  const list = ins ?? [];
  const userIds = Array.from(new Set(list.map((i) => i.usuario_id)));
  const { data: profs } = userIds.length
    ? await supabase.from("profiles").select("id,full_name").in("id", userIds)
    : { data: [] as { id: string; full_name: string | null }[] };
  const nameMap = new Map((profs ?? []).map((p) => [p.id, p.full_name ?? ""]));
  const result: InscritosMap = {};
  list.forEach((i) => {
    const slot = (result[i.evento_id] ??= { count: 0, names: [] });
    slot.count += 1;
    if (slot.names.length < 3) slot.names.push(nameMap.get(i.usuario_id) ?? "");
  });
  return result;
}
type Role = "runner" | "organizer" | "cafe";
type Inscrito = { usuario_id: string; fecha_inscripcion: string; profiles: { full_name: string | null } | null };

function Dashboard() {
  const { active: activeCtx, managed, ready } = useAppContexts();
  const [role, setRole] = useState<Role>("runner");
  const [userId, setUserId] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return;
      setUserId(u.user.id);
      const { data: prof } = await supabase
        .from("profiles")
        .select("role")
        .eq("id", u.user.id)
        .maybeSingle();
      if (prof?.role) setRole(prof.role as Role);
      setLoaded(true);
    })();
  }, []);

  if (!loaded || !userId) {
    return <div className="mx-auto max-w-6xl px-4 md:px-6 py-20 text-muted-foreground">Cargando...</div>;
  }

  // El contexto activo (cafe_managers / club_managers) manda cuando existe.
  if (ready && managed.length > 0) {
    if (activeCtx.kind === "club") return <OrganizerDashboard userId={userId} clubId={activeCtx.id} />;
    if (activeCtx.kind === "cafe") return <CafeRedirect />;
    return <RunnerDashboard userId={userId} />;
  }

  if (role === "runner") return <RunnerDashboard userId={userId} />;
  if (role === "organizer") return <OrganizerDashboard userId={userId} />;
  return <CafeDashboard userId={userId} />;
}

function CafeRedirect() {
  useEffect(() => {
    window.location.assign("/cafe/dashboard");
  }, []);
  return <div className="mx-auto max-w-6xl px-4 md:px-6 py-20 text-muted-foreground">Abriendo tu café...</div>;
}

/* ------------------------- RUNNER ------------------------- */

type CafeRow = {
  id: string;
  name: string;
  location: string;
  city: string | null;
  hours: string | null;
  member_discount: string | null;
  google_maps_url: string | null;
};

function mapsLink(c: CafeRow) {
  if (c.google_maps_url) return c.google_maps_url;
  const q = [c.name, c.location, c.city || "Aguascalientes"].filter(Boolean).join(", ");
  return `https://maps.google.com/?q=${encodeURIComponent(q)}`;
}

function RunnerDashboard({ userId }: { userId: string }) {
  const [events, setEvents] = useState<EventRow[]>([]);
  const [myInscriptions, setMyInscriptions] = useState<Set<string>>(new Set());
  const [inscritosMap, setInscritosMap] = useState<InscritosMap>({});
  const [firstName, setFirstName] = useState("");
  const [stats, setStats] = useState({ events: 0, clubs: 0, cafes: 0 });
  const [cafes, setCafes] = useState<CafeRow[]>([]);
  const [cityFilter, setCityFilter] = useState<string>("all");

  const load = async () => {
    const today = new Date().toISOString().split("T")[0];
    const weekFromNow = new Date();
    weekFromNow.setDate(weekFromNow.getDate() + 7);
    const weekISO = weekFromNow.toISOString().split("T")[0];

    const [evRes, inRes, profRes, clubsCount, cafesCount, cafesRes] = await Promise.all([
      supabase
        .from("events")
        .select(EVENT_COLS)
        .gte("event_date", today)
        .order("event_date"),
      supabase.from("inscripciones").select("evento_id").eq("usuario_id", userId),
      supabase.from("profiles").select("full_name").eq("id", userId).maybeSingle(),
      supabase.from("clubs").select("*", { count: "exact", head: true }),
      supabase.from("cafes").select("*", { count: "exact", head: true }),
      supabase
        .from("cafes")
        .select("id,name,location,city,hours,member_discount,google_maps_url")
        .order("name"),
    ]);
    const list = ((evRes.data ?? []) as unknown as EventRow[]).filter((e) => e.estado_confirmacion !== "declinado" && !e.cancelado);
    setEvents(list);
    setMyInscriptions(new Set((inRes.data ?? []).map((i: { evento_id: string }) => i.evento_id)));
    setInscritosMap(await fetchInscritosMap(list.map((e) => e.id)));
    const fullName = profRes.data?.full_name ?? "";
    setFirstName(fullName.split(" ")[0]);
    const cafesList = (cafesRes.data ?? []) as CafeRow[];
    setCafes(cafesList);
    // Default city filter to user's profile city if it has cafes available
    const { data: meProf } = await supabase.from("profiles").select("city").eq("id", userId).maybeSingle();
    const myCity = meProf?.city ?? null;
    if (myCity && cafesList.some((c) => c.city === myCity)) {
      setCityFilter(myCity);
    }
    setStats({
      events: list.filter((e) => e.event_date <= weekISO).length,
      clubs: clubsCount.count ?? 0,
      cafes: cafesCount.count ?? 0,
    });
  };

  useEffect(() => {
    load();
  }, [userId]);

  const [pendingJoin, setPendingJoin] = useState<string | null>(null);

  const afterJoin = async (eventoId: string) => {
    setMyInscriptions((s) => new Set(s).add(eventoId));
    setInscritosMap(await fetchInscritosMap(events.map((e) => e.id)));
  };

  const myEvents = events.filter((e) => myInscriptions.has(e.id));
  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Buenos días";
    if (h < 19) return "Buenas tardes";
    return "Buenas noches";
  })();

  return (
    <div className="mx-auto max-w-6xl px-4 md:px-6 py-12 space-y-12">
      <div>
        <p className="text-sm text-muted-foreground mb-2">
          {greeting}{firstName ? `, ${firstName}` : ""}
        </p>
        <h1 className="font-display text-4xl sm:text-5xl md:text-6xl">Tu próxima salida</h1>
        <div className="mt-5 inline-flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-muted-foreground">
          <span><span className="text-foreground font-semibold">{stats.events}</span> eventos esta semana</span>
          <span className="text-primary">·</span>
          <span><span className="text-foreground font-semibold">{stats.clubs}</span> clubs disponibles</span>
          <span className="text-primary">·</span>
          <span><span className="text-foreground font-semibold">{stats.cafes}</span> locales cerca</span>
        </div>
      </div>

      <section>
        {events.length === 0 ? (
          <Empty icon={<Calendar className="w-6 h-6" />} text="Aún no hay eventos próximos." />
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {events.map((ev) => (
              <EventCard
                key={ev.id}
                ev={ev}
                inscritos={inscritosMap[ev.id]}
                action={
                  myInscriptions.has(ev.id) ? (
                    <button
                      disabled
                      className="px-3 py-2 min-h-10 rounded-md bg-primary/20 text-primary text-sm font-semibold flex items-center gap-1.5"
                    >
                      <Check className="w-3.5 h-3.5" /> Inscrito
                    </button>
                  ) : (
                    <button
                      onClick={() => setPendingJoin(ev.id)}
                      className="px-4 py-2 min-h-10 inline-flex items-center rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90"
                    >
                      Unirme
                    </button>
                  )
                }
              />
            ))}
          </div>
        )}
      </section>

      <div className="flex items-center gap-4">
        <div className="h-px flex-1 bg-border/60" />
        <span className="text-xs uppercase tracking-[0.3em] text-muted-foreground">mis eventos</span>
        <div className="h-px flex-1 bg-border/60" />
      </div>

      <section>
        {myEvents.length === 0 ? (
          <Empty icon={<Calendar className="w-6 h-6" />} text="Aún no te has unido a ningún evento." />
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {myEvents.map((ev) => (
              <EventCard key={ev.id} ev={ev} inscritos={inscritosMap[ev.id]} />
            ))}
          </div>
        )}
      </section>

      <div className="flex items-center gap-4">
        <div className="h-px flex-1 bg-border/60" />
        <span className="text-xs uppercase tracking-[0.3em] text-muted-foreground">puntos brulo cerca de ti</span>
        <div className="h-px flex-1 bg-border/60" />
      </div>

      <section>
        {(() => {
          const cities = Array.from(new Set(cafes.map((c) => c.city).filter((c): c is string => !!c))).sort();
          const filtered = cityFilter === "all" ? cafes : cafes.filter((c) => c.city === cityFilter);
          return (
            <>
              {cities.length > 0 && (
                <div className="mb-5 flex items-center gap-2 flex-wrap">
                  <label htmlFor="city-filter" className="text-sm text-muted-foreground">Ciudad:</label>
                  <select
                    id="city-filter"
                    value={cityFilter}
                    onChange={(e) => setCityFilter(e.target.value)}
                    className="bg-card border border-border rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="all">Todas las ciudades</option>
                    {cities.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                  <span className="text-xs text-muted-foreground">
                    {filtered.length} {filtered.length === 1 ? "punto" : "puntos"}
                  </span>
                </div>
              )}
              {filtered.length === 0 ? (
                <Empty
                  icon={<Coffee className="w-6 h-6" />}
                  text={
                    cafes.length === 0
                      ? "Aún no hay puntos Brulo en tu ciudad — ¡pronto habrá más!"
                      : `Aún no hay puntos Brulo en ${cityFilter}.`
                  }
                />
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  {filtered.map((c) => (
                    <div key={c.id} className="bg-card border border-border rounded-lg p-5 flex flex-col">
                      <div className="flex items-start gap-3">
                        <Coffee className="w-5 h-5 text-primary shrink-0 mt-1" />
                        <div className="flex-1 min-w-0">
                          <h3 className="font-display text-xl leading-tight">{c.name}</h3>
                          <p className="text-sm text-muted-foreground mt-1">
                            {c.location}{c.city ? ` · ${c.city}` : ""}
                          </p>
                        </div>
                      </div>
                      <div className="mt-4 space-y-1.5 text-sm text-muted-foreground">
                        {c.hours && (
                          <div className="flex items-center gap-2">
                            <Clock className="w-3.5 h-3.5" /> <span>{c.hours}</span>
                          </div>
                        )}
                        {c.member_discount && (
                          <div className="flex items-start gap-2">
                            <Check className="w-3.5 h-3.5 mt-0.5 text-primary" />
                            <span>{c.member_discount}</span>
                          </div>
                        )}
                      </div>
                      <a
                        href={mapsLink(c)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-4 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90"
                      >
                        <MapPin className="w-4 h-4" /> Cómo llegar
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </>
          );
        })()}
      </section>

      <JoinConsentDialog
        eventId={pendingJoin}
        eventName={events.find((e) => e.id === pendingJoin)?.name ?? null}
        userId={userId}
        onClose={() => setPendingJoin(null)}
        onJoined={afterJoin}
      />
    </div>
  );
}
/* ----------------------- ORGANIZER ----------------------- */

function OrganizerDashboard({ userId, clubId }: { userId: string; clubId?: string }) {
  const [events, setEvents] = useState<EventRow[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [openEventId, setOpenEventId] = useState<string | null>(null);
  const [inscritos, setInscritos] = useState<Inscrito[]>([]);
  const [club, setClub] = useState<{ id: string; name: string; type: string; city: string | null; slug: string | null } | null>(null);
  const [cafeNames, setCafeNames] = useState<Record<string, string>>({});
  const [invitations, setInvitations] = useState<Array<{
    id: string;
    estado: "pendiente" | "aceptado" | "declinado";
    evento: EventRow & { cafes: { name: string } | null };
  }>>([]);

  useEffect(() => {
    (async () => {
      const clubQuery = clubId
        ? supabase.from("clubs").select("id,name,type,city,slug").eq("id", clubId).maybeSingle()
        : supabase.from("clubs").select("id,name,type,city,slug").eq("owner_id", userId).maybeSingle();
      const [{ data: clubData }, { data: evs }] = await Promise.all([
        clubQuery,
        supabase
          .from("events")
          .select(EVENT_COLS)
          .eq("organizer_id", userId)
          .order("event_date"),
      ]);
      setClub(clubData);
      const list = (evs ?? []) as unknown as EventRow[];
      setEvents(list);
      // Cargar nombres de cafés para mostrar en banners de declinados
      const cafeIds = Array.from(new Set(list.filter((e) => e.cafe_id).map((e) => e.cafe_id as string)));
      if (cafeIds.length) {
        const { data: cf } = await supabase.from("cafes").select("id,name").in("id", cafeIds);
        const map: Record<string, string> = {};
        (cf ?? []).forEach((c: { id: string; name: string }) => { map[c.id] = c.name; });
        setCafeNames(map);
      }
      if (list.length) {
        const { data: ins } = await supabase
          .from("inscripciones")
          .select("evento_id")
          .in(
            "evento_id",
            list.map((e) => e.id),
          );
        const c: Record<string, number> = {};
        (ins ?? []).forEach((i: { evento_id: string }) => {
          c[i.evento_id] = (c[i.evento_id] ?? 0) + 1;
        });
        setCounts(c);
      }

      // Cargar invitaciones para este organizador (todos sus clubs)
      if (clubData?.id) {
        const { data: invs } = await supabase
          .from("invitaciones_clubs")
          .select(`id,estado,events(${EVENT_COLS},cafes(name))`)
          .eq("club_id", clubData.id)
          .order("created_at", { ascending: false });
        setInvitations(
          ((invs ?? []) as unknown as Array<{
            id: string;
            estado: "pendiente" | "aceptado" | "declinado";
            events: EventRow & { cafes: { name: string } | null };
          }>).map((i) => ({ id: i.id, estado: i.estado, evento: i.events })),
        );
      }
    })();
  }, [userId]);

  const respondInvitation = async (id: string, estado: "aceptado" | "declinado") => {
    const { error } = await supabase
      .from("invitaciones_clubs")
      .update({ estado, responded_at: new Date().toISOString() })
      .eq("id", id);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(estado === "aceptado" ? "Invitación aceptada" : "Invitación declinada");
    setInvitations((prev) => prev.map((inv) => (inv.id === id ? { ...inv, estado } : inv)));
  };



  const openInscritos = async (eventoId: string) => {
    setOpenEventId(eventoId);
    const { data: ins } = await supabase
      .from("inscripciones")
      .select("usuario_id,fecha_inscripcion")
      .eq("evento_id", eventoId)
      .order("fecha_inscripcion");
    const list = ins ?? [];
    if (list.length === 0) {
      setInscritos([]);
      return;
    }
    const { data: profs } = await supabase
      .from("profiles")
      .select("id,full_name")
      .in("id", list.map((i) => i.usuario_id));
    const map = new Map((profs ?? []).map((p) => [p.id, p.full_name]));
    setInscritos(
      list.map((i) => ({
        usuario_id: i.usuario_id,
        fecha_inscripcion: i.fecha_inscripcion,
        profiles: { full_name: map.get(i.usuario_id) ?? null },
      })),
    );
  };

  const today = new Date();
  const todayISO = today.toISOString().split("T")[0];
  const activeEvents = events.filter((e) => !e.cancelado);
  const cancelledEvents = events.filter((e) => e.cancelado);
  const upcomingActive = activeEvents
    .filter((e) => e.event_date >= todayISO)
    .sort((a, b) => a.event_date.localeCompare(b.event_date));
  const pastActive = activeEvents
    .filter((e) => e.event_date < todayISO)
    .sort((a, b) => b.event_date.localeCompare(a.event_date));
  const totalInscritos = Object.values(counts).reduce((a, b) => a + b, 0);
  const monthEvents = activeEvents.filter((e) => {
    const d = new Date(e.event_date);
    return d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear();
  }).length;
  const nextEvent = upcomingActive[0];
  const daysToNext = nextEvent
    ? Math.max(0, Math.round((new Date(nextEvent.event_date).getTime() - today.getTime()) / 86400000))
    : null;
  const clubTypeLabel =
    club?.type === "ciclismo" ? "Ciclismo"
    : club?.type === "trail" ? "Trail"
    : club?.type === "caminata" ? "Caminata"
    : club?.type === "multideporte" ? "Multideporte"
    : club?.type === "ride" ? "Ciclismo"
    : club?.type === "both" ? "Multideporte"
    : "Running";
  const ClubTypeIcon = club?.type === "ciclismo" || club?.type === "ride"
    ? Bike
    : club?.type === "trail" ? Mountain
    : club?.type === "caminata" ? PersonStanding
    : Footprints;
  const [cancelConfirmId, setCancelConfirmId] = useState<string | null>(null);
  const [showCancelled, setShowCancelled] = useState(false);
  const [showPast, setShowPast] = useState(false);
  const [activeTab, setActiveTab] = useState<"events" | "club" | "requests">("events");

  const confirmCancel = async () => {
    if (!cancelConfirmId) return;
    const evt = events.find((e) => e.id === cancelConfirmId);
    const { error } = await supabase.from("events").update({ cancelado: true }).eq("id", cancelConfirmId);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Evento cancelado");
    setEvents((prev) => prev.map((e) => (e.id === cancelConfirmId ? { ...e, cancelado: true } : e)));

    // Notify registered attendees by email (best-effort)
    if (evt) {
      try {
        const { attendees } = await getEventAttendeeEmails({ data: { eventId: cancelConfirmId } });
        if (attendees.length) {
          const dateStr = new Date(`${evt.event_date}T00:00:00`).toLocaleDateString("es-MX", {
            weekday: "long", day: "numeric", month: "long",
          });
          const organizerName = evt.clubs?.name ?? "El organizador";
          await Promise.all(
            attendees.map((p) =>
              sendTransactionalEmail({
                templateName: "event-cancelled",
                recipientEmail: p.email,
                idempotencyKey: `event-cancelled-${cancelConfirmId}-${p.id}`,
                templateData: {
                  eventName: evt.name,
                  eventDate: dateStr,
                  eventTime: evt.event_time?.slice(0, 5),
                  organizerName,
                },
                context: { eventId: cancelConfirmId },
              }).catch((err) => console.warn("cancel email failed", err)),
            ),
          );
        }
      } catch (err) {
        console.warn("Notify attendees failed", err);
      }
    }
    setCancelConfirmId(null);
  };


  return (
    <div className="mx-auto max-w-6xl px-4 md:px-6 py-12 space-y-8 relative">
      <Link
        to="/events/new"
        className="hidden md:inline-flex sticky top-6 z-30 float-right items-center gap-2 px-6 py-3 rounded-md bg-primary text-primary-foreground font-semibold shadow-lg shadow-primary/30 hover:opacity-90 transition"
      >
        + Nueva salida
      </Link>

      <div>
        <p className="text-primary text-xs tracking-[0.3em] uppercase mb-3">organizador</p>
        <h1 className="font-display text-4xl sm:text-5xl md:text-6xl leading-tight">{club?.name ?? "Tu club"}</h1>
        <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider bg-background/60 border border-primary/30 text-primary">
            <ClubTypeIcon className="w-3.5 h-3.5" />
            {clubTypeLabel}
          </span>
          {club?.city && <span>{club.city}</span>}
        </div>
        <Link
          to="/events/new"
          className="md:hidden mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary text-primary-foreground font-semibold"
        >
          + Nueva salida
        </Link>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <OrgStat icon={<Users className="w-7 h-7" />} value={totalInscritos} label="total inscritos" highlight />
        <OrgStat icon={<Calendar className="w-7 h-7" />} value={monthEvents} label="eventos este mes" />
        <OrgStat
          icon={<Clock className="w-7 h-7" />}
          value={daysToNext}
          label={
            daysToNext === null
              ? "sin próximo evento"
              : daysToNext === 0
                ? "tu próximo evento es hoy"
                : `días al próximo evento`
          }
        />
      </div>

      {/* Tabs */}
      <div>
        <div className="flex border-b border-border">
          {[
            { key: "events" as const, label: "Mis eventos" },
            { key: "club" as const, label: "Mi club" },
            { key: "requests" as const, label: "Solicitudes" },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`relative px-4 py-3 text-sm font-semibold transition ${
                activeTab === t.key
                  ? "text-primary"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {t.label}
              {activeTab === t.key && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />
              )}
            </button>
          ))}
        </div>

        <div className="pt-6">
          {activeTab === "events" && (
            <div className="space-y-8">
              {invitations.length > 0 && (
                <section>
                  <h2 className="font-display text-2xl mb-4 flex items-center gap-2">
                    <Coffee className="w-5 h-5 text-primary" /> Invitaciones de cafeterías
                  </h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    {invitations.map((inv) => {
                      const ev = inv.evento;
                      if (!ev) return null;
                      const cafeName = ev.cafes?.name ?? "Una cafetería";
                      const dateStr = new Date(`${ev.event_date}T00:00:00`).toLocaleDateString("es-MX", {
                        weekday: "long",
                        day: "numeric",
                        month: "long",
                      });
                      return (
                        <div
                          key={inv.id}
                          className={`rounded-lg p-5 border ${
                            inv.estado === "pendiente"
                              ? "bg-amber-500/5 border-amber-500/30"
                              : inv.estado === "aceptado"
                                ? "bg-primary/5 border-primary/30"
                                : "bg-muted/30 border-border"
                          }`}
                        >
                          <p className="text-xs uppercase tracking-[0.2em] text-primary mb-2">
                            {cafeName} te invita
                          </p>
                          <h3 className="font-display text-xl mb-1 break-words">{ev.name}</h3>
                          <p className="text-sm text-muted-foreground capitalize">
                            {dateStr} · {ev.event_time?.slice(0, 5)}
                          </p>
                          {inv.estado === "pendiente" ? (
                            <div className="mt-4 flex gap-2">
                              <button
                                onClick={() => respondInvitation(inv.id, "aceptado")}
                                className="flex-1 min-h-10 px-3 py-2 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 inline-flex items-center justify-center gap-1.5"
                              >
                                <Check className="w-4 h-4" /> Aceptar
                              </button>
                              <button
                                onClick={() => respondInvitation(inv.id, "declinado")}
                                className="flex-1 min-h-10 px-3 py-2 rounded-md bg-muted text-muted-foreground text-sm font-semibold hover:bg-muted/80"
                              >
                                Declinar
                              </button>
                            </div>
                          ) : (
                            <p className="mt-3 text-xs font-semibold uppercase tracking-wider">
                              {inv.estado === "aceptado" ? (
                                <span className="text-primary">✓ Evento confirmado</span>
                              ) : (
                                <span className="text-muted-foreground">Declinado</span>
                              )}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}

              {events.filter((e) => e.estado_confirmacion === "declinado").map((ev) => (
                <Link
                  key={`decl-${ev.id}`}
                  to="/eventos/$eventId"
                  params={{ eventId: ev.id }}
                  className="block bg-red-500/10 border border-red-500/40 rounded-lg p-5 hover:bg-red-500/15 transition"
                >
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                    <p className="text-sm">
                      <span className="font-semibold text-red-400">
                        {ev.cafe_id ? cafeNames[ev.cafe_id] ?? "La cafetería" : "La cafetería"}
                      </span>{" "}
                      <span className="text-muted-foreground">declinó tu solicitud para</span>{" "}
                      <span className="font-semibold">{ev.name}</span>
                      <span className="text-muted-foreground"> — elige otro punto de reunión</span>
                    </p>
                  </div>
                </Link>
              ))}

              {upcomingActive.length === 0 && pastActive.length === 0 ? (
                <Empty icon={<Calendar className="w-6 h-6" />} text="Aún no has creado eventos. Crea el primero." />
              ) : (
                <div className="space-y-6">
                  {/* Upcoming events */}
                  {upcomingActive.length > 0 && (
                    <section>
                      <h2 className="font-display text-xl mb-4 flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-primary" />
                        Próximas salidas
                      </h2>
                      <div className="grid md:grid-cols-2 gap-4">
                        {upcomingActive.map((ev) => (
                        <EventCard
                            key={ev.id}
                            ev={ev}
                            footer={
                              <div className="mt-4 pt-4 border-t border-border space-y-3">
                                <Link
                                  to="/evento/$eventId/seguridad"
                                  params={{ eventId: ev.id }}
                                  className="inline-flex w-full items-center justify-center gap-2 px-4 py-2.5 rounded-md border-2 border-primary bg-primary/10 text-primary text-sm font-semibold hover:bg-primary/20"
                                >
                                  <ShieldAlert className="w-4 h-4" /> Roster de emergencia
                                </Link>
                                <div className="flex items-center justify-between gap-2">
                                  <span className="text-sm text-muted-foreground">
                                    <strong className="text-foreground">{counts[ev.id] ?? 0}</strong> inscritos
                                  </span>
                                  <button
                                    onClick={() => openInscritos(ev.id)}
                                    className="text-sm text-primary hover:underline"
                                  >
                                    Ver lista
                                  </button>
                                </div>
                                <div className="flex gap-2 relative z-20">
                                  <Link
                                    to="/events/new"
                                    search={{ editId: ev.id }}
                                    className="flex-1 min-h-10 px-3 py-2 rounded-md border border-border text-sm font-semibold inline-flex items-center justify-center gap-1.5 hover:bg-muted/50"
                                  >
                                    <Pencil className="w-3.5 h-3.5" /> Editar
                                  </Link>
                                  <button
                                    onClick={() => setCancelConfirmId(ev.id)}
                                    className="flex-1 min-h-10 px-3 py-2 rounded-md border border-red-500/40 text-red-400 text-sm font-semibold inline-flex items-center justify-center gap-1.5 hover:bg-red-500/10"
                                  >
                                    <X className="w-3.5 h-3.5" /> Cancelar evento
                                  </button>
                                </div>
                              </div>
                            }
                          />
                        ))}
                      </div>
                    </section>
                  )}

                  {/* Past events accordion */}
                  {pastActive.length > 0 && (
                    <section>
                      <button
                        onClick={() => setShowPast((v) => !v)}
                        className="w-full flex items-center justify-between gap-2 px-4 py-3 rounded-md border border-border text-sm font-semibold text-muted-foreground hover:bg-muted/30"
                      >
                        <span>Eventos pasados ({pastActive.length})</span>
                        {showPast ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                      {showPast && (
                        <div className="grid md:grid-cols-2 gap-4 mt-4">
                          {pastActive.map((ev) => (
                            <EventCard
                              key={ev.id}
                              ev={ev}
                              footer={
                                <div className="mt-4 pt-4 border-t border-border">
                                  <Link
                                    to="/evento/$eventId/seguridad"
                                    params={{ eventId: ev.id }}
                                    className="inline-flex w-full items-center justify-center gap-2 px-4 py-2.5 rounded-md border border-primary/40 bg-primary/10 text-primary text-sm font-semibold hover:bg-primary/20"
                                  >
                                    <ShieldAlert className="w-4 h-4" /> Roster de emergencia
                                  </Link>
                                </div>
                              }
                            />
                          ))}
                        </div>
                      )}
                    </section>
                  )}
                </div>
              )}

              {/* Cancelled events accordion */}
              {cancelledEvents.length > 0 && (
                <section>
                  <button
                    onClick={() => setShowCancelled((v) => !v)}
                    className="w-full flex items-center justify-between gap-2 px-4 py-3 rounded-md border border-border text-sm font-semibold text-muted-foreground hover:bg-muted/30"
                  >
                    <span>Eventos cancelados ({cancelledEvents.length})</span>
                    {showCancelled ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  {showCancelled && (
                    <div className="grid md:grid-cols-2 gap-4 mt-4">
                      {cancelledEvents.map((ev) => (
                        <EventCard
                          key={ev.id}
                          ev={ev}
                          footer={
                            <div className="mt-4 pt-4 border-t border-border">
                              <Link
                                to="/evento/$eventId/seguridad"
                                params={{ eventId: ev.id }}
                                className="inline-flex w-full items-center justify-center gap-2 px-4 py-2.5 rounded-md border border-primary/40 bg-primary/10 text-primary text-sm font-semibold hover:bg-primary/20"
                              >
                                <ShieldAlert className="w-4 h-4" /> Roster de emergencia
                              </Link>
                            </div>
                          }
                        />
                      ))}
                    </div>
                  )}
                </section>
              )}
            </div>
          )}

          {activeTab === "club" && club?.id && (
            <div className="space-y-6">
              {/* Tu página pública */}
              <section className="bg-card border border-border rounded-xl p-6">
                <h2 className="font-display text-xl mb-4 flex items-center gap-2">
                  <ExternalLink className="w-5 h-5 text-primary" />
                  Tu página pública
                </h2>
                <p className="text-sm text-muted-foreground mb-4">
                  Comparte este enlace para reclutar nuevos miembros a tu club.
                </p>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                  <div className="flex-1 min-w-0 bg-background border border-border rounded-lg px-4 py-2.5 text-sm text-foreground truncate">
                    https://brulo.fit/club/{club.slug ?? club.id}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(`https://brulo.fit/club/${club.slug ?? club.id}`);
                        toast.success("¡Enlace copiado!");
                      }}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-muted text-foreground text-sm font-semibold hover:bg-muted/80 transition"
                    >
                      <Copy className="w-4 h-4" />
                      Copiar enlace
                    </button>
                    <a
                      href={`https://brulo.fit/club/${club.slug ?? club.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Ver mi página
                    </a>
                  </div>
                </div>
              </section>

              <CustomizeClubSection clubId={club.id} />
            </div>
          )}

          {activeTab === "requests" && club?.id && <JoinRequestsSection clubId={club.id} clubName={club.name} />}
        </div>
      </div>

      {cancelConfirmId && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50"
          onClick={() => setCancelConfirmId(null)}
        >
          <div
            className="bg-card border border-border rounded-lg p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-display text-2xl mb-3">Cancelar evento</h3>
            <p className="text-sm text-muted-foreground mb-6">
              ¿Estás seguro que quieres cancelar este evento? Los inscritos serán notificados.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setCancelConfirmId(null)}
                className="flex-1 min-h-11 px-3 py-2 rounded-md border border-border text-sm font-semibold hover:bg-muted/50"
              >
                Volver
              </button>
              <button
                onClick={confirmCancel}
                className="flex-1 min-h-11 px-3 py-2 rounded-md bg-red-500 text-white text-sm font-semibold hover:bg-red-600"
              >
                Sí, cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {openEventId && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50"
          onClick={() => setOpenEventId(null)}
        >
          <div
            className="bg-card border border-border rounded-lg p-6 max-w-md w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-display text-2xl mb-4">Inscritos</h3>
            {inscritos.length === 0 ? (
              <p className="text-muted-foreground">Aún no hay inscritos.</p>
            ) : (
              <ul className="space-y-3">
                {inscritos.map((i) => (
                  <li
                    key={i.usuario_id}
                    className="flex items-center justify-between border-b border-border pb-2"
                  >
                    <span>{i.profiles?.full_name ?? "Corredor"}</span>
                    <span className="text-xs text-muted-foreground">
                      {new Date(i.fecha_inscripcion).toLocaleDateString("es-ES", {
                        day: "numeric",
                        month: "short",
                      })}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <button
              onClick={() => setOpenEventId(null)}
              className="mt-6 w-full py-2 rounded-md bg-muted hover:bg-muted/80"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------- CAFE ------------------------- */

function CafeDashboard({ userId }: { userId: string }) {
  const [events, setEvents] = useState<EventRow[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [weekTotal, setWeekTotal] = useState(0);

  useEffect(() => {
    (async () => {
      const { data: cafe } = await supabase
        .from("cafes")
        .select("id")
        .eq("owner_id", userId)
        .maybeSingle();
      if (!cafe) return;
      const today = new Date().toISOString().split("T")[0];
      const { data: evs } = await supabase
        .from("events")
        .select(EVENT_COLS)
        .eq("cafe_id", cafe.id)
        .gte("event_date", today)
        .order("event_date");
      const list = ((evs ?? []) as unknown as EventRow[]).filter((e) => !e.cancelado);

      setEvents(list);

      if (list.length) {
        const { data: ins } = await supabase
          .from("inscripciones")
          .select("evento_id")
          .in(
            "evento_id",
            list.map((e) => e.id),
          );
        const c: Record<string, number> = {};
        (ins ?? []).forEach((i: { evento_id: string }) => {
          c[i.evento_id] = (c[i.evento_id] ?? 0) + 1;
        });
        setCounts(c);

        const weekFromNow = new Date();
        weekFromNow.setDate(weekFromNow.getDate() + 7);
        const weekISO = weekFromNow.toISOString().split("T")[0];
        const weekly = list
          .filter((e) => e.event_date <= weekISO)
          .reduce((acc, e) => acc + (c[e.id] ?? 0), 0);
        setWeekTotal(weekly);
      }
    })();
  }, [userId]);

  return (
    <div className="mx-auto max-w-6xl px-4 md:px-6 py-12 space-y-12">
      <Header eyebrow="cafetería brulo" title="Tu agenda Brulo" />

      <div className="bg-card border border-border rounded-lg p-8">
        <p className="text-xs uppercase tracking-[0.3em] text-primary mb-2">esta semana</p>
        <p className="font-display text-4xl">
          Recibes <span className="text-primary">{weekTotal}</span> corredores y ciclistas
        </p>
      </div>

      <section>
        <h2 className="font-display text-3xl mb-6 flex items-center gap-3">
          <Coffee className="w-6 h-6 text-primary" /> Eventos en tu cafetería
        </h2>
        {events.length === 0 ? (
          <Empty
            icon={<Coffee className="w-6 h-6" />}
            text="Aún no hay eventos con tu cafetería como punto de reunión."
          />
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {events.map((ev) => (
              <EventCard
                key={ev.id}
                ev={ev}
                footer={
                  <div className="mt-4 pt-4 border-t border-border text-sm">
                    <strong className="text-primary">{counts[ev.id] ?? 0}</strong>{" "}
                    <span className="text-muted-foreground">asistentes esperados</span>
                  </div>
                }
              />
            ))}
          </div>
        )}
      </section>

      <Link
        to="/cafe/perfil"
        className="inline-block text-sm text-primary hover:underline"
      >
        Editar perfil de cafetería →
      </Link>
    </div>
  );
}

/* ----------------------- SHARED UI ----------------------- */

function Header({ eyebrow, title }: { eyebrow: string; title: string }) {
  return (
    <div>
      <p className="text-primary text-xs tracking-[0.3em] uppercase mb-2">{eyebrow}</p>
      <h1 className="font-display text-4xl sm:text-5xl md:text-6xl">{title}</h1>
    </div>
  );
}

function EventCard({
  ev,
  action,
  footer,
  inscritos,
}: {
  ev: EventRow;
  action?: React.ReactNode;
  footer?: React.ReactNode;
  inscritos?: { count: number; names: string[] };
}) {
  const isCafeEvent = ev.organizador_tipo === "cafeteria";
  const ta = ev.tipo_actividad;
  // Resolve activity from event-level tipo_actividad first, fall back to club type for legacy data.
  const activity: "running" | "ciclismo" | "trail" | "caminata" | "otro" | "social" | "ambos" =
    ta === "ciclismo" || ta === "trail" || ta === "caminata" || ta === "running" || ta === "otro" || ta === "social" || ta === "ambos"
      ? ta
      : (ev.clubs?.type === "ride" || ev.clubs?.type === "ciclismo") ? "ciclismo"
      : ev.clubs?.type === "trail" ? "trail"
      : ev.clubs?.type === "caminata" ? "caminata"
      : ev.clubs?.type === "both" ? "ambos"
      : "running";
  const typeLabel =
    activity === "ciclismo" ? "Ciclismo"
    : activity === "trail" ? "Trail"
    : activity === "caminata" ? "Caminata"
    : activity === "social" ? "Social"
    : activity === "ambos" ? "Mixto"
    : activity === "otro" ? "Otro"
    : "Running";
  const TypeIcon =
    activity === "ciclismo" ? Bike
    : activity === "trail" ? Mountain
    : activity === "caminata" ? PersonStanding
    : activity === "otro" ? Dumbbell
    : Footprints;

  const dateLabel = new Date(`${ev.event_date}T00:00:00`).toLocaleDateString("es-ES", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  return (
    <article className="group relative w-full max-w-full min-w-0 bg-card border border-border rounded-xl p-5 sm:p-6 transition-all hover:border-primary/50 hover:shadow-[0_0_24px_-8px_hsl(var(--primary)/0.4)] overflow-hidden">
      <Link
        to="/eventos/$eventId"
        params={{ eventId: ev.id }}
        className="absolute inset-0 rounded-xl z-0"
        aria-label={`Ver ${ev.name}`}
      />
      <div className="relative z-10 pointer-events-none">
        <div className="flex items-start justify-between gap-3 mb-3">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider bg-background/60 border border-primary/30 text-primary">
            <TypeIcon className="w-3.5 h-3.5" />
            {typeLabel}
          </span>
          {ev.distance_km != null && (
            <span className="font-display text-3xl leading-none text-primary">
              {ev.distance_km}
              <span className="text-sm ml-0.5">km</span>
            </span>
          )}
        </div>
        {ev.cancelado && (
          <div className="mb-3">
            <span className="inline-block text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-1 rounded bg-red-500 text-white">
              Cancelado
            </span>
          </div>
        )}
        {isCafeEvent && !ev.cancelado && (
          <div className="mb-3">
            <span className="inline-block text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-1 rounded bg-[oklch(0.7_0.18_25)] text-white">
              Evento del local
            </span>
          </div>
        )}
        {!isCafeEvent && ev.cafe_invitado_id && !ev.cancelado && (
          <div className="mb-3">
            <span className="inline-block text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-1 rounded bg-yellow-500 text-black">
              Café pendiente de confirmar
            </span>
          </div>
        )}
        {!isCafeEvent && ev.cafe_id && !ev.cancelado && (

          <div className="mb-3">
            <EventStatusBadge estado={ev.estado_confirmacion} createdAt={ev.created_at} />
          </div>
        )}
        {ev.clubs?.name && (
          <p className="text-[10px] uppercase tracking-[0.3em] text-primary font-bold mb-2">
            {ev.clubs.name}
          </p>
        )}
        <h3 className="font-display text-2xl text-foreground leading-tight mb-4 break-words">{ev.name}</h3>
        <div className="h-px bg-border/60 mb-4" />
        <div className="text-sm text-muted-foreground space-y-2">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-primary/70 shrink-0" />
            <span className="capitalize">{dateLabel}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-primary/70 shrink-0" />
            {ev.event_time.slice(0, 5)}
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-primary/70 shrink-0" />
            <span className="truncate">{ev.meeting_point}</span>
          </div>
        </div>
      </div>

      <div className="relative z-10 mt-5 pt-4 border-t border-border/60 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 pointer-events-none">
          {inscritos && inscritos.count > 0 ? (
            <>
              <div className="flex -space-x-2">
                {inscritos.names.slice(0, 3).map((n, i) => (
                  <div
                    key={i}
                    className="w-7 h-7 rounded-full border-2 border-card bg-primary/20 text-primary text-[10px] font-bold flex items-center justify-center"
                  >
                    {(n || "?").slice(0, 1).toUpperCase()}
                  </div>
                ))}
              </div>
              <span className="text-xs text-muted-foreground">
                <span className="text-foreground font-semibold">{inscritos.count}</span>{" "}
                {inscritos.count === 1 ? "va" : "van"}
              </span>
            </>
          ) : (
            <span className="text-xs text-muted-foreground">Sé el primero</span>
          )}
        </div>
        <div className="pointer-events-auto flex items-center gap-2">
          <ShareEventButton
            event={{
              id: ev.id,
              name: ev.name,
              event_date: ev.event_date,
              event_time: ev.event_time,
              meeting_point: ev.meeting_point,
              distance_km: ev.distance_km,
              club_name: ev.clubs?.name ?? null,
            }}
          />
          {action}
        </div>
      </div>
      {footer && <div className="relative z-10">{footer}</div>}
    </article>
  );
}

function Empty({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <div className="bg-card/50 border border-dashed border-border rounded-lg p-12 text-center text-muted-foreground">
      <div className="inline-flex p-3 rounded-full bg-muted mb-3">{icon}</div>
      <p>{text}</p>
    </div>
  );
}

function OrgStat({
  icon,
  value,
  label,
  highlight,
}: {
  icon: React.ReactNode;
  value: number | null;
  label: string;
  highlight?: boolean;
}) {
  return (
    <div className="bg-card border border-border rounded-xl p-6 transition-colors hover:border-primary/40">
      <div
        className={`mb-3 inline-flex p-2.5 rounded-lg ${highlight ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}
      >
        {icon}
      </div>
      <p className={`font-display text-5xl ${highlight ? "text-primary" : "text-foreground"}`}>
        {value ?? "—"}
      </p>
      <p className="text-xs text-muted-foreground mt-2 uppercase tracking-widest">{label}</p>
    </div>
  );
}

/* ------------------------- JOIN REQUESTS (organizer) ------------------------- */

type JoinRequestRow = {
  id: string;
  user_id: string;
  message: string | null;
  status: "pending" | "approved" | "rejected";
  created_at: string;
  profiles: { full_name: string | null; city: string | null; disciplinas: string[] | null } | null;
};

function JoinRequestsSection({ clubId, clubName }: { clubId: string; clubName: string }) {
  const [requests, setRequests] = useState<JoinRequestRow[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = async () => {
    const { data } = await supabase
      .from("club_join_requests")
      .select("id,user_id,message,status,created_at")
      .eq("club_id", clubId)
      .eq("status", "pending")
      .order("created_at", { ascending: false });
    const list = (data ?? []) as Array<Omit<JoinRequestRow, "profiles">>;
    if (list.length === 0) { setRequests([]); return; }
    const { data: profs } = await supabase
      .from("profiles")
      .select("id,full_name,city,disciplinas")
      .in("id", list.map((r) => r.user_id));
    const profMap = new Map((profs ?? []).map((p: any) => [p.id, p]));
    setRequests(list.map((r) => ({
      ...r,
      profiles: profMap.get(r.user_id) ?? null,
    })));
  };

  useEffect(() => { load(); }, [clubId]);

  const respond = async (req: JoinRequestRow, status: "approved" | "rejected") => {
    setBusyId(req.id);
    const { error } = await supabase
      .from("club_join_requests")
      .update({ status })
      .eq("id", req.id);
    if (error) {
      toast.error(error.message);
      setBusyId(null);
      return;
    }
    toast.success(status === "approved" ? "Solicitud aprobada" : "Solicitud rechazada");
    setRequests((prev) => prev.filter((r) => r.id !== req.id));
    try {
      const recipient = await getJoinRequestRecipient({ data: { requestId: req.id } });
      await sendTransactionalEmail({
        templateName: status === "approved" ? "club-join-approved" : "club-join-rejected",
        recipientEmail: recipient.email,
        idempotencyKey: `club-join-${status}-${req.id}`,
        templateData: { clubName: recipient.clubName, athleteName: recipient.athleteName },
        context: { clubId: recipient.clubId },
      });
    } catch (err) {
      console.warn("Join request notification failed", err);
    }
    setBusyId(null);
  };

  if (requests.length === 0) {
    return (
      <section>
        <h2 className="font-display text-2xl mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-primary" /> Solicitudes para unirse a {clubName}
        </h2>
        <Empty icon={<Users className="w-6 h-6" />} text="No hay solicitudes pendientes." />
      </section>
    );
  }

  return (
    <section>
      <h2 className="font-display text-2xl mb-4 flex items-center gap-2">
        <Users className="w-5 h-5 text-primary" /> Solicitudes para unirse a {clubName}
      </h2>
      <div className="grid md:grid-cols-2 gap-4">
        {requests.map((r) => {
          const name = r.profiles?.full_name ?? "Atleta";
          const city = r.profiles?.city;
          const disciplinas = r.profiles?.disciplinas ?? [];
          return (
            <div key={r.id} className="rounded-lg border border-primary/30 bg-primary/5 p-5">
              <h3 className="font-display text-xl">{name}</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {[city].filter(Boolean).join(" · ")}
              </p>
              {disciplinas.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {disciplinas.map((d) => (
                    <span key={d} className="text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-background/60 border border-border text-muted-foreground">
                      {d}
                    </span>
                  ))}
                </div>
              )}
              {r.message && (
                <p className="mt-3 text-sm text-foreground/90 italic border-l-2 border-primary/40 pl-3">
                  "{r.message}"
                </p>
              )}
              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => respond(r, "approved")}
                  disabled={busyId === r.id}
                  className="flex-1 min-h-10 px-3 py-2 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 disabled:opacity-60 inline-flex items-center justify-center gap-1.5"
                >
                  <Check className="w-4 h-4" /> Aprobar
                </button>
                <button
                  onClick={() => respond(r, "rejected")}
                  disabled={busyId === r.id}
                  className="flex-1 min-h-10 px-3 py-2 rounded-md border border-red-500/40 text-red-400 text-sm font-semibold hover:bg-red-500/10 disabled:opacity-60 inline-flex items-center justify-center gap-1.5"
                >
                  <X className="w-4 h-4" /> Rechazar
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
